import { ComponentProps, useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  PatientDataItemName,
  SnackBarType,
  StatusLabelForCsSystemType,
} from "../../../types";
import { defaultTheme } from "../../themes";
import { PageBody } from "../PageBody";
import { Typography } from "../../atoms/Typography";
import { List, ListItem } from "../../atoms/List";
import { hexToRGB } from "../../../utils/style";
import { Snackbar } from "../../atoms/Snackbar";
import okIcon from "../../../assets/icons/ok.png";
import errorIcon from "../../../assets/icons/alert.png";
import { StatusLabelMap } from "../../../utils/prescription";

/**
 * 処方詳細ページ
 */

type PatientHt = {
  patientId: string;
  nickname: string;
  memberImage: any;
  birthday: string;
  appName: string;
  email?: string | null;
  prescriptionCode: string;
  prescribedAt: string;
  medicalInstitutionId?: string | null | undefined;
  medicalInstitutionName?: string | null | undefined;
  prescription?: {
    name?: string | null;
    prescriptionCureAppPdtCode?: {
      statusLabelForCsSystem?: StatusLabelForCsSystemType | null;
    };
  };
};

export type PatientHtProps = {
  patientHt: PatientHt;
  patientItems?: PatientDataItemName[];
  onUpdateEmailAddressButtonClick: () => void;
  onPublishVerifyCodeButtonClick: () => void;
  onCancelPrescriptionCodeButtonClick: () => void;
  snackBar: SnackBarType;
  maskedDetail: object;
};

const Menu = styled.div`
  padding: 10px 0;
  width: auto;
`;

const MenuInner = styled.div`
  cursor: pointer;
  padding: 0 30px;
  display: flex;
  flex-flow: column nowrap;
`;

const MenuItem = styled.div`
  margin: 15px 0;
  display: flex;
  align-items: center;
`;

const MenuLink = styled.a`
  margin: 15px 0;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${defaultTheme.colors.primaryText};
`;

const ContentWrapper = styled.div`
  padding: 0 40px;
`;

const Inner = styled.div`
  padding: 40px 0 20px;
  display: flex;
  ${(props) => hexToRGB(props.theme.colors.primaryBorder, 0.5)};
`;

const LeftBlock = styled.div`
  flex: 1;
  padding-right: 40px;
`;

const MemberIconAndName = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-right: 20px;
  }
`;

const RightBlock = styled.div`
  flex: 1;
  padding-left: 40px;
`;

const PaddingRightWrapper = styled.div`
  width: 100%;
  padding-right: 20px;
`;

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.colors.brand};
`;

const TextRightTypography = (props: ComponentProps<typeof Typography>) => (
  <PaddingRightWrapper>
    <Typography textRight size={2}>
      {props.children}
    </Typography>
  </PaddingRightWrapper>
);

export function PatientHtDetailPage({
  patientHt,
  onUpdateEmailAddressButtonClick,
  onPublishVerifyCodeButtonClick,
  onCancelPrescriptionCodeButtonClick,
  maskedDetail,
  snackBar,
}: PatientHtProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const maskedDetailBlob = new Blob([JSON.stringify(maskedDetail)], {
    type: "text/json",
  });
  const jsonURL = URL.createObjectURL(maskedDetailBlob);

  return (
    <>
      <PageBody
        title="処方詳細"
        buttonTitle="メニュー"
        buttonIcon={
          <FontAwesomeIcon icon={"pen"} color={defaultTheme.colors.brand} />
        }
        onButtonClick={() => {
          setIsMenuOpen(!isMenuOpen);
        }}
        menuListVisible={isMenuOpen}
        menuList={
          <Menu>
            <MenuInner>
              <MenuItem onClick={onUpdateEmailAddressButtonClick}>
                <Typography size={2}>メールアドレスの変更</Typography>
              </MenuItem>
              <MenuItem onClick={onPublishVerifyCodeButtonClick}>
                <Typography size={2}>確認番号の発行</Typography>
              </MenuItem>
              <MenuItem onClick={onCancelPrescriptionCodeButtonClick}>
                <Typography size={2}>処方のキャンセル</Typography>
              </MenuItem>
              <MenuLink href={jsonURL} download={"maskedDetail.json"}>
                <Typography size={2}>jsonファイル出力</Typography>
              </MenuLink>
            </MenuInner>
          </Menu>
        }
        onHideMenu={() => {
          setIsMenuOpen(false);
        }}
      >
        <>
          <ContentWrapper>
            <Inner>
              <LeftBlock>
                <MemberIconAndName>
                  <img
                    src={patientHt.memberImage}
                    width="72"
                    height="72"
                    alt={"user_icon"}
                  />
                  <List>
                    <ListItem>
                      <Typography bold size={1}>
                        ニックネーム
                      </Typography>
                      <Typography size={5}>{patientHt.nickname}</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography bold size={1}>
                        氏名
                      </Typography>
                      <Typography size={3}>
                        {patientHt.prescription?.name}
                      </Typography>
                    </ListItem>
                  </List>
                </MemberIconAndName>
              </LeftBlock>
            </Inner>
          </ContentWrapper>

          <ContentWrapper>
            <Inner>
              <LeftBlock>
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      id
                    </Typography>
                    <TextRightTypography>
                      {patientHt.patientId}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      メールアドレス
                    </Typography>
                    <TextRightTypography>{patientHt.email}</TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方日
                    </Typography>
                    <TextRightTypography>
                      {patientHt.prescribedAt}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      アプリ名
                    </Typography>
                    <TextRightTypography>
                      {patientHt.appName}
                    </TextRightTypography>
                  </ListItem>
                </List>
              </LeftBlock>
              <RightBlock>
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      生年月日
                    </Typography>
                    <TextRightTypography>
                      {patientHt.birthday}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方コード
                    </Typography>
                    <TextRightTypography>
                      {patientHt.prescriptionCode}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方医療機関
                    </Typography>
                    <TextRightTypography>
                      <StyledLink
                        to={`/medicalInstitution/${patientHt.medicalInstitutionId}`}
                      >
                        {patientHt.medicalInstitutionName}
                      </StyledLink>
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方コードステータス
                    </Typography>
                    <TextRightTypography>
                      {
                        StatusLabelMap[
                          patientHt.prescription?.prescriptionCureAppPdtCode
                            ?.statusLabelForCsSystem as keyof typeof StatusLabelMap
                        ]
                      }
                    </TextRightTypography>
                  </ListItem>
                </List>
              </RightBlock>
            </Inner>
          </ContentWrapper>
        </>
      </PageBody>

      {!!snackBar && (
        <Snackbar
          isOpen={snackBar.isOpen}
          icon={
            <img
              src={snackBar.type === "ok" ? okIcon : errorIcon}
              width="18"
              height="18"
              alt={snackBar.type}
            />
          }
          text={snackBar.message}
          close={snackBar.close}
        />
      )}
    </>
  );
}
